import { audioManager } from '@modules/audio/AudioManager.js';

/**
 * MenuManager class for managing the application's menu system
 * Implements singleton pattern with private fields for robust state management
 */
class MenuManager {
  // Private static field for singleton instance
  static #instance = null;

  // Private instance fields
  #isMenuOpen = false;
  #elements = {};
  #eventListeners = [];
  #initialized = false;
  #menuItems = [];
  #categories = [];

  // Default configuration options
  static get DEFAULTS() {
    return {
      containerId: 'menu-container',
      toggleId: 'menu-toggle',
      overlayClass: 'menu-overlay',
      menuOpenClass: 'menu-open',
      activeClass: 'active',
      parentElementSelector: 'body',
    };
  }

  // Custom events
  static get EVENTS() {
    return {
      MENU_OPEN: 'menu:open',
      MENU_CLOSE: 'menu:close',
      MENU_TOGGLE: 'menu:toggle',
      CATEGORY_CHANGE: 'menu:category-change',
    };
  }

  /**
   * Get the singleton instance of MenuManager
   * @returns {MenuManager} The singleton instance
   */
  static getInstance() {
    if (!MenuManager.#instance) {
      MenuManager.#instance = new MenuManager();
    }
    return MenuManager.#instance;
  }

  /**
   * Private constructor to enforce singleton pattern
   */
  constructor(options = {}) {
    if (MenuManager.#instance) {
      return MenuManager.#instance;
    }
    MenuManager.#instance = this;

    // Merge default options with provided options
    this.options = { ...MenuManager.DEFAULTS, ...options };

    // Bind methods
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.toggleMenu = this.toggleMenu.bind(this);
    this.addMenuItem = this.addMenuItem.bind(this);
    this.addCategory = this.addCategory.bind(this);
    this.showCategory = this.showCategory.bind(this);
    this.cleanup = this.cleanup.bind(this);

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  /**
   * Initialize the menu system
   * @returns {Promise<boolean>} True if initialization was successful, false otherwise
   */
  async init() {
    if (this.#initialized) {
      console.warn('MenuManager already initialized');
      return true;
    }

    try {
      // Wait for audio initialization if audioManager exists
      if (audioManager && typeof audioManager.init === 'function') {
        console.log('AudioManager available for MenuManager');
      } else {
        console.warn('AudioManager not available, proceeding without audio');
      }

      // Setup menu elements and event listeners
      this.#setupMenu();
      this.#setupEventListeners();
      this.#initialized = true;
      console.log('MenuManager initialized successfully');

      // Tambahkan kategori default jika belum ada
      if (this.#categories.length === 0) {
        this.addCategory({ name: 'Planets & Sun', id: 'planets-sun', info: 'Explore the planets and the Sun in our solar system.', controls: '☀️: Sun | ☿: Mercury | ♀️: Venus | 🌍: Earth | ♂️: Mars | ♃: Jupiter | ♄: Saturn | ♅: Uranus | ♆: Neptune' });
        this.addCategory({ name: 'Space Objects', id: 'space-objects', info: 'Discover human-made and natural objects that orbit around our solar system, from satellites to comets.', controls: '💫: Asteroid Belt | 🛰️: ISS | ☄️: Comet | 📡: Satellite | 🛸: UFO | 🚀: Rocket | 🚀✨: Starship | 🌙: Moon' });
        this.addCategory({ name: 'Controls & Navigation', id: 'controls-nav', info: 'Learn how to navigate and control the Milkyway Explorer simulation.', controls: '🎮: Mouse/Touch | ⌨️: Keyboard | 🪐⬅️/🪐➡️: Navigate planets | 🔄: Reset view | ⟳: Auto-rotate | 🎥: Camera tracking | ⬅️/➡️: Rotate | 🔍+/🔍-: Zoom' });
        // Tampilkan kategori pertama secara default
        this.showCategory('planets-sun');
      }

      return true;
    } catch (error) {
      console.error('Failed to initialize MenuManager:', error);
      return false;
    }
  }

  /**
   * Set up menu DOM elements
   * @private
   */
  #setupMenu() {
    const parentElement = document.querySelector(this.options.parentElementSelector) || document.body;

    // Create or get menu container
    let container = document.getElementById(this.options.containerId);
    if (!container) {
      container = document.createElement('div');
      container.id = this.options.containerId;
      container.className = 'menu-container';
      container.setAttribute('aria-hidden', 'true');
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 300px;\n
        height: 100%;
        background: rgba(15, 23, 42, 0.95);\n
        color: #fff;
        padding: 20px;
        box-shadow: 2px 0 10px rgba(0, 0, 0, 0.5);\n
        transform: translateX(-100%);\n
        transition: transform 0.3s ease-in-out;\n
        z-index: 1000;\n
        overflow-y: auto;\n
        -webkit-backdrop-filter: blur(8px);\n
        backdrop-filter: blur(8px);\n
      `;
      parentElement.appendChild(container);
    }
    this.#elements.container = container;

    // Create menu content area
    let contentArea = container.querySelector('.menu-content-area');
    if (!contentArea) {
      contentArea = document.createElement('div');
      contentArea.className = 'menu-content-area';
      container.appendChild(contentArea);
    }
    this.#elements.contentArea = contentArea;

    // Create or get toggle button
    let toggle = document.getElementById(this.options.toggleId);
    if (!toggle) {
      toggle = document.createElement('button');
      toggle.id = this.options.toggleId;
      toggle.className = 'menu-toggle';
      toggle.setAttribute('aria-label', 'Toggle menu');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', this.options.containerId);
      toggle.innerHTML = '☰';
      toggle.style.cssText = `
        position: fixed;
        top: 20px;
        left: 20px;\n
        z-index: 1001;
        background: rgba(0, 0, 0, 0.8);\n
        color: #fff;\n
        border: none;\n
        padding: 10px 15px;\n
        font-size: 1.5em;\n
        border-radius: 5px;\n
        cursor: pointer;\n
        transition: background-color 0.3s ease;\n
      `;
      parentElement.appendChild(toggle);
    }
    this.#elements.toggle = toggle;

    // Tambahkan elemen untuk daftar kategori
    let categoryList = container.querySelector('.menu-category-list');
    if (!categoryList) {
      categoryList = document.createElement('div');
      categoryList.className = 'menu-category-list';
      categoryList.style.cssText = `
        margin-bottom: 20px;\n
        padding-bottom: 10px;\n
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);\n
      `;
      container.insertBefore(categoryList, contentArea);
    }
    this.#elements.categoryList = categoryList;

    // Tambahkan elemen untuk judul kategori dan info/kontrol
    let categoryHeader = container.querySelector('.menu-category-header');
    if (!categoryHeader) {
      categoryHeader = document.createElement('h3');
      categoryHeader.className = 'menu-category-header';
      categoryHeader.style.cssText = `
        color: #00ccff;\n
        margin-top: 0;\n
      `;
      contentArea.appendChild(categoryHeader);
    }
    this.#elements.categoryHeader = categoryHeader;

    let categoryInfo = container.querySelector('.menu-category-info');
    if (!categoryInfo) {
      categoryInfo = document.createElement('p');
      categoryInfo.className = 'menu-category-info';
      contentArea.appendChild(categoryInfo);
    }
    this.#elements.categoryInfo = categoryInfo;

    let categoryControls = container.querySelector('.menu-category-controls');
    if (!categoryControls) {
      categoryControls = document.createElement('p');
      categoryControls.className = 'menu-category-controls';
      categoryControls.style.cssText = `
        background: rgba(0, 30, 70, 0.5);\n
        padding: 10px;\n
        border-radius: 8px;\n
        margin-top: 15px;\n
      `;
      contentArea.appendChild(categoryControls);
    }
    this.#elements.categoryControls = categoryControls;

    // Kosongkan area konten menu untuk diisi nanti
    contentArea.innerHTML = '';

    // Tambahkan efek hover pada tombol toggle
    toggle.addEventListener('mouseenter', () => {
      toggle.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
    });
    toggle.addEventListener('mouseleave', () => {
      toggle.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    });
  }

  /**
   * Set up event listeners for menu interactions
   * @private
   */
  #setupEventListeners() {
    const { toggle, container } = this.#elements;

    // Toggle menu on button click
    this.#addEvent(toggle, 'click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleMenu();
    });

    // Close menu on document click
    this.#addEvent(document, 'click', this.handleDocumentClick);

    // Close menu on escape key
    this.#addEvent(document, 'keydown', this.handleEscapeKey);

    // Cleanup on page unload
  }

  /**
   * Add an event listener with cleanup tracking
   * @param {EventTarget} element - The target element
   * @param {string} type - The event type
   * @param {Function} handler - The event handler
   * @private
   */
  #addEvent(element, type, handler) {
    if (!element || !type || typeof handler !== 'function') {
      console.warn('Invalid event listener parameters:', { element, type, handler });
      return;
    }
    element.addEventListener(type, handler);
    this.#eventListeners.push({ element, type, handler });
  }

  /**
   * Handle document click events to close the menu
   * @param {Event} event - The click event
   * @private
   */
  handleDocumentClick(event) {
    const { container, toggle } = this.#elements;
    if (this.#isMenuOpen && !container.contains(event.target) && !toggle.contains(event.target)) {
      this.closeMenu();
    }
  }

  /**
   * Handle escape key to close the menu
   * @param {KeyboardEvent} event - The keydown event
   * @private
   */
  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.#isMenuOpen) {
      this.closeMenu();
    }
  }

  /**
   * Toggle the menu visibility
   * @returns {boolean} True if the menu is now open, false if closed
   */
  toggleMenu() {
    if (!this.#initialized) {
      console.warn('MenuManager not initialized');
      return false;
    }
    const newState = !this.#isMenuOpen;
    if (newState) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
    this.#dispatchEvent(MenuManager.EVENTS.MENU_TOGGLE, { isOpen: newState });
    return newState;
  }

  /**
   * Open the menu
   * @returns {boolean} True if the menu was opened
   */
  openMenu() {
    if (!this.#initialized || this.#isMenuOpen) return false;

    const { container, toggle } = this.#elements;
    container.style.transform = 'translateX(0)';
    container.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    container.classList.add(this.options.menuOpenClass);
    this.#isMenuOpen = true;

    // Putar suara jika audioManager tersedia
    if (audioManager && typeof audioManager.playSound === 'function') {
      audioManager.playSound('click');
    }

    this.#dispatchEvent(MenuManager.EVENTS.MENU_OPEN);
    console.log('Menu opened');
    return true;
  }

  /**
   * Close the menu
   * @returns {boolean} True if the menu was closed
   */
  closeMenu() {
    if (!this.#initialized || !this.#isMenuOpen) return false;

    const { container, toggle } = this.#elements;
    container.style.transform = 'translateX(-100%)';
    container.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    container.classList.remove(this.options.menuOpenClass);
    this.#isMenuOpen = false;

    // Putar suara jika audioManager tersedia
    if (audioManager && typeof audioManager.playSound === 'function') {
      audioManager.playSound('click');
    }

    this.#dispatchEvent(MenuManager.EVENTS.MENU_CLOSE);
    console.log('Menu closed');
    return true;
  }

  /**
   * Add a menu item to a category
   * @param {Object} itemConfig - Configuration for the menu item
   * @param {string} itemConfig.label - The text label for the menu item
   * @param {string} itemConfig.categoryId - The ID of the category to add the item to
   * @param {Function} itemConfig.onClick - The function to call when the item is clicked
   * @param {string} [itemConfig.icon=''] - Optional icon for the menu item
   * @param {string} [itemConfig.className=''] - Optional CSS class for the menu item
   */
  addMenuItem({ label, categoryId, onClick, icon = '', className = '' }) {
    if (!label || !categoryId || typeof onClick !== 'function') {
      console.error('Invalid menu item configuration', { label, categoryId, onClick });
      return;
    }

    const category = this.#categories.find(cat => cat.id === categoryId);
    if (!category) {
      console.error(`Category with ID ${categoryId} not found.`);
      return;
    }

    const menuItem = document.createElement('button');
    menuItem.className = `menu-item ${className}`.trim();
    menuItem.innerHTML = `${icon ? `<span class="menu-item-icon">${icon}</span>` : ''}<span class="menu-item-label">${label}</span>`;
    menuItem.style.cssText = `
      display: block;\n
      width: 100%;\n
      text-align: left;\n
      padding: 10px;\n
      border: none;\n
      background: transparent;\n
      color: #fff;\n
      cursor: pointer;\n
      font-size: 1em;\n
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);\n
      transition: background-color 0.2s ease;\n
    `;

    // Tambahkan event listener untuk item menu
    this.#addEvent(menuItem, 'click', () => {
      onClick();
      this.closeMenu();
      // Putar suara jika audioManager tersedia
      if (audioManager && typeof audioManager.playSound === 'function') {
        audioManager.playSound('click');
      }
    });

    // Tambahkan efek hover
    menuItem.addEventListener('mouseenter', () => {
      menuItem.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
    });
    menuItem.addEventListener('mouseleave', () => {
      menuItem.style.backgroundColor = 'transparent';
    });

    // Simpan referensi item menu ke kategori
    if (!category.items) category.items = [];
    category.items.push({ element: menuItem, onClick });

    // Jika kategori sedang ditampilkan, render ulang konten kategori
    if (this.#elements.contentArea && this.#elements.contentArea.dataset.categoryId === categoryId) {
       this.#renderCategoryContent(categoryId);
    }
  }

  /**
   * Add a category to the menu
   * @param {Object} categoryConfig - Configuration for the category
   * @param {string} categoryConfig.name - The display name for the category
   * @param {string} categoryConfig.id - A unique ID for the category
   * @param {string} [categoryConfig.info=''] - Information text for the category
   * @param {string} [categoryConfig.controls=''] - Controls information for the category
   */
  addCategory({ name, id, info = '', controls = '' }) {
    if (!name || !id) {
      console.error('Category name and id are required', { name, id });
      return;
    }

    if (this.#categories.some(cat => cat.id === id)) {
      console.warn(`Category with ID ${id} already exists.`);
      return;
    }

    const category = { name, id, info, controls, items: [] };
    this.#categories.push(category);

    // Buat tombol untuk kategori di daftar kategori
    const categoryButton = document.createElement('button');
    categoryButton.className = 'menu-category-button';
    categoryButton.textContent = name;
    categoryButton.style.cssText = `
      display: block;\n
      width: 100%;\n
      text-align: left;\n
      padding: 10px;\n
      border: none;\n
      background: transparent;\n
      color: #fff;\n
      cursor: pointer;\n
      font-size: 1.1em;\n
      font-weight: bold;\n
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);\n
      transition: background-color 0.2s ease;\n
    `;

     // Tambahkan event listener untuk tombol kategori
    this.#addEvent(categoryButton, 'click', () => {
      this.showCategory(id);
       // Putar suara jika audioManager tersedia
      if (audioManager && typeof audioManager.playSound === 'function') {
        audioManager.playSound('click');
      }
    });

     // Tambahkan efek hover
    categoryButton.addEventListener('mouseenter', () => {
      categoryButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
    });
    categoryButton.addEventListener('mouseleave', () => {
      categoryButton.style.backgroundColor = 'transparent';
    });

    this.#elements.categoryList.appendChild(categoryButton);
  }

  /**
   * Render the content of a specific category in the content area
   * @param {string} categoryId - The ID of the category to render
   * @private
   */
  #renderCategoryContent(categoryId) {
      const category = this.#categories.find(cat => cat.id === categoryId);
      if (!category) {
        console.error(`Category with ID ${categoryId} not found.`);
        return;
      }

      const { contentArea, categoryHeader, categoryInfo: infoElement, categoryControls: controlsElement } = this.#elements;

      // Kosongkan area konten
      contentArea.innerHTML = '';
      contentArea.dataset.categoryId = categoryId; // Simpan ID kategori aktif

      // Tambahkan judul kategori
      categoryHeader.textContent = category.name;
      contentArea.appendChild(categoryHeader);

      // Tambahkan info kategori
      infoElement.textContent = category.info;
      contentArea.appendChild(infoElement);

      // Tambahkan info kontrol jika ada
      if (category.controls) {
        controlsElement.textContent = category.controls;
         controlsElement.style.display = 'block'; // Pastikan elemen kontrol ditampilkan
        contentArea.appendChild(controlsElement);
      } else {
         controlsElement.style.display = 'none'; // Sembunyikan elemen kontrol jika tidak ada
      }

      // Tambahkan item menu untuk kategori ini
      if (category.items && category.items.length > 0) {
        const itemsList = document.createElement('div');
        itemsList.style.marginTop = '15px';
        category.items.forEach(item => itemsList.appendChild(item.element));
        contentArea.appendChild(itemsList);
      }

      // Tandai tombol kategori yang aktif
      if (this.#elements.categoryList) {
          Array.from(this.#elements.categoryList.children).forEach(button => {
            if (button.textContent === category.name) {
              button.classList.add(this.options.activeClass);
              button.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'; // Tambahkan visual indikator aktif
            } else {
              button.classList.remove(this.options.activeClass);
              button.style.backgroundColor = 'transparent'; // Hapus visual indikator non-aktif
            }
          });
      }
  }

  /**
   * Show the content for a specific category
   * @param {string} categoryId - The ID of the category to show
   */
  showCategory(categoryId) {
    const category = this.#categories.find(cat => cat.id === categoryId);
    if (!category) {
      console.error(`Category with ID ${categoryId} not found.`);
      return;
    }

    this.#renderCategoryContent(categoryId); // Render konten kategori

    this.#dispatchEvent(MenuManager.EVENTS.CATEGORY_CHANGE, { categoryId });
    console.log(`Showing category: ${category.name}`);
  }

  /**
   * Dispatch a custom event
   * @param {string} type - The event type
   * @param {Object} [detail={}] - Event detail data
   * @private
   */
  #dispatchEvent(type, detail = {}) {
    const event = new CustomEvent(type, { detail });
    document.dispatchEvent(event);
  }

  /**
   * Clean up event listeners and DOM elements
   */
  cleanup() {
    // Hapus event listener
    this.#eventListeners.forEach(({ element, type, handler }) => {
      if (element && typeof element.removeEventListener === 'function') {
        element.removeEventListener(type, handler);
      }
    });
    this.#eventListeners = [];

    // Hapus elemen DOM menu jika ada
    if (this.#elements.container && this.#elements.container.parentNode) {
      this.#elements.container.parentNode.removeChild(this.#elements.container);
    }
    if (this.#elements.toggle && this.#elements.toggle.parentNode) {
      this.#elements.toggle.parentNode.removeChild(this.#elements.toggle);
    }

    this.#elements = {};
    this.#initialized = false;
    this.#isMenuOpen = false;
    this.#menuItems = [];
    this.#categories = [];

    console.log('MenuManager cleaned up');
  }

  /**
   * Get the current state of the menu
   * @returns {Object} The menu state
   */
  getState() {
    return {
      isOpen: this.#isMenuOpen,
      initialized: this.#initialized,
    };
  }

  // Optional: Add methods to add sections, headers, etc. if needed
}

// Create and export the singleton instance
export const menuManager = MenuManager.getInstance();
export default menuManager; 